import { useState, useEffect, useRef } from 'react';
import { selectPageId } from '../store/index';
import { ModuleBase, EntityCard } from 'components';
import { useTranslation } from 'next-i18next';
import { useSelector } from 'react-redux';
import { fixColor, isColorDark, pushValuesToRouteQuery } from '@/utils';
import { useRouter } from 'next/router';
import classNames from 'classnames';

const isRoomCardType = (cardType) => cardType === 1 || cardType === 'Room' || cardType === 'room';

const CardListingVerticalModule = ({ data }) => {
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [roomsExhausted, setRoomsExhausted] = useState(false);
  const fetchController = useRef(null);
  const initializedKey = useRef(null);
  const resortPage = useRef(1);
  const pageId = useSelector(selectPageId);
  const router = useRouter();
  const { t } = useTranslation('common');

  const isRoomListing = isRoomCardType(data?.filtersAndCards?.cardType);

  useEffect(() => {
    if (!data?.filtersAndCards?.cards || !pageId) {
      return;
    }

    const initKey = `${pageId}-${data.moduleId}`;
    if (initializedKey.current === initKey) {
      return;
    }

    initializedKey.current = initKey;
    resortPage.current = 1;
    setRoomsExhausted(false);
    setCards(data.filtersAndCards.cards);
    setTotalCount(data.filtersAndCards.totalCount ?? 0);
  }, [
    pageId,
    data?.moduleId,
    data?.filtersAndCards?.cards,
    data?.filtersAndCards?.totalCount,
    data?.filtersAndCards?.cardType,
  ]);

  const loadMore = () => {
    if (loading) {
      return;
    }

    if (isRoomListing) {
      if (roomsExhausted) {
        return;
      }
      getRoomLoadMore();
      return;
    }

    if (cards.length >= totalCount) {
      return;
    }

    getPagedLoadMore();
  };

  const getRoomLoadMore = async () => {
    setLoading(true);
    if (fetchController.current) {
      fetchController.current.abort();
    }
    const controller = new AbortController();
    fetchController.current = controller;

    const pageSize = data.filtersAndCards.pageSize || 3;
    const existingIds = new Set(cards.map((card) => card.moduleId));
    let page = resortPage.current;
    let foundNewCards = [];
    let lastTotalCount = totalCount;

    try {
      // Same resort only (ancestorId). No room-type filter → Lagoon/Villa/etc. of THIS hotel.
      for (let attempt = 0; attempt < 20; attempt += 1) {
        const reqData = {
          pageId: pageId,
          culture: data.filtersAndCards?.culture || router.locale,
          ancestorId: data.filtersAndCards.ancestorId,
          pageSize,
          pageNumber: page,
          cardsType: data.filtersAndCards.cardType,
          filters: [],
        };

        const dataRes = await fetch(`/api/umbraco/api/ProductCard/GetCards`, {
          method: 'POST',
          signal: controller.signal,
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(reqData),
        });

        if (!dataRes?.ok) {
          setRoomsExhausted(true);
          break;
        }

        const responseData = await dataRes.json();
        const nextCards = responseData.cards || [];

        if (responseData.totalCount != null) {
          lastTotalCount = responseData.totalCount;
        }

        const newCards = nextCards.filter((card) => card.moduleId && !existingIds.has(card.moduleId));

        if (newCards.length > 0) {
          foundNewCards = newCards;
          resortPage.current = page + 1;
          if (nextCards.length < pageSize) {
            setRoomsExhausted(true);
          }
          break;
        }

        if (nextCards.length < pageSize) {
          setRoomsExhausted(true);
          break;
        }

        page += 1;
        resortPage.current = page;
      }

      if (foundNewCards.length > 0) {
        setCards((prev) => [...prev, ...foundNewCards]);
        pushValuesToRouteQuery(router, { page: resortPage.current });
      }

      setTotalCount(lastTotalCount);
    } catch (error) {
      if (error?.name !== 'AbortError') {
        console.error(error);
      }
    } finally {
      setLoading(false);
    }
  };

  const getPagedLoadMore = async () => {
    setLoading(true);
    if (fetchController.current) {
      fetchController.current.abort();
    }
    const controller = new AbortController();
    fetchController.current = controller;

    const nextPage = Math.floor(cards.length / (data.filtersAndCards.pageSize || 3)) + 1;

    const reqData = {
      pageId: pageId,
      culture: data.filtersAndCards?.culture || router.locale,
      ancestorId: data.filtersAndCards.ancestorId,
      pageSize: data.filtersAndCards.pageSize,
      pageNumber: nextPage,
      cardsType: data.filtersAndCards.cardType,
    };

    try {
      const dataRes = await fetch(`/api/umbraco/api/ProductCard/GetCards`, {
        method: 'POST',
        signal: controller.signal,
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(reqData),
      });

      if (dataRes?.ok) {
        const responseData = await dataRes.json();
        setCards((prev) => [...prev, ...(responseData.cards || [])]);
        setTotalCount(responseData.totalCount ?? totalCount);
        pushValuesToRouteQuery(router, { page: nextPage });
      }
    } catch (error) {
      if (error?.name !== 'AbortError') {
        console.error(error);
      }
    } finally {
      setLoading(false);
    }
  };

  const dark = isColorDark(fixColor(data?.backgroundColour));

  // Room listings: show Load More until no more rooms remain in THIS resort.
  const hasMoreCards = isRoomListing ? !roomsExhausted : totalCount > 0 && cards.length < totalCount;

  return (
    <ModuleBase data={data} className="overflow-hidden py-6 md:py-10 lg:py-20">
      <div className="container flex flex-col gap-8 md:gap-12 lg:gap-10 items-center">
        {cards.map((card, index) => (
          <EntityCard
            key={card.moduleId ?? index}
            data={card}
            dark={dark}
            showButton={data?.filtersAndCards?.showBookNowButton}
            className={classNames(
              'w-full px-0',
              card.props.alignment === 'Side' ? 'odd:lg:flex-row even:lg:flex-row-reverse' : '',
            )}
          />
        ))}

        {loading && (
          <div className="w-full flex justify-center py-10">
            <svg
              className={classNames('animate-spin h-10 w-10 ', dark ? 'text-white' : 'text-black')}
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              />
            </svg>
          </div>
        )}

        {hasMoreCards && (
          <button className="color-from-bg btn link w-fit px-16" onClick={loadMore} disabled={loading}>
            {t('general.$viewMore')}
          </button>
        )}
      </div>
    </ModuleBase>
  );
};
export default CardListingVerticalModule;
